import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/components/ThemeProvider";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, 
  Briefcase, 
  FileText, 
  BarChart3, 
  Settings, 
  Menu, 
  X, 
  Building2,
  FileBarChart,
  Sun,
  Moon,
  Clock,
  CalendarDays,
  MessageSquare,
  Users,
  BookOpen,
  FileCheck,
  Package,
  Archive,
  Gift
} from "lucide-react";
import NotificationCenter from "@/components/NotificationCenter";
import TradeIQIcon from "@/components/icons/TradeIQIcon";
import WhatsNewModal, { useWhatsNew } from "@/components/WhatsNewModal";
import { useSubscription } from "@/components/SubscriptionContext";
import { Sidebar } from "@/components/layout/Sidebar";
import { DesktopTopBar } from "@/components/layout/DesktopTopBar";
import api from "@/lib/api";

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { hasFeature } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebarCollapsed") === "1");
  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("sidebarCollapsed", next ? "1" : "0");
      return next;
    });
  };
  const [currentTime, setCurrentTime] = useState(new Date());
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadPortalMessages, setUnreadPortalMessages] = useState(0);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const { hasUnseen: hasNewUpdates } = useWhatsNew();

  // Live clock - updates every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Unread messages poll
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await api.get("/messages/unread");
        setUnreadMessages(res.data?.total || 0);
      } catch (err) { console.debug("Handled error:", err); }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, []);

  // Unread portal messages poll (for CRM badge)
  useEffect(() => {
    const fetchPortalUnread = async () => {
      try {
        const res = await api.get("/crm/portal-messages/unread-total");
        setUnreadPortalMessages(res.data?.unread || 0);
      } catch (err) { console.debug("Handled error:", err); }
    };
    fetchPortalUnread();
    const interval = setInterval(fetchPortalUnread, 20000);
    return () => clearInterval(interval);
  }, []);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      second: '2-digit',
      hour12: true 
    });
  };

  const isActive = (path) => {
    if (path.includes('?tab=')) {
      const [base, query] = path.split('?');
      const tab = new URLSearchParams(query).get('tab');
      return location.pathname === base && new URLSearchParams(location.search).get('tab') === tab;
    }
    if (path === '/admin' && location.pathname === '/admin') {
      return !location.search || new URLSearchParams(location.search).get('tab') === null;
    }
    return location.pathname === path;
  };

  const ownerNavItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/jobs", label: "Jobs", icon: Briefcase },
    { path: "/schedule", label: "Schedule", icon: CalendarDays },
    { path: "/customers", label: "Customers", icon: Users, feature: "crm" },
    { path: "/messages", label: "Messages", icon: MessageSquare, feature: "crew_messaging" },
    { path: "/invoices", label: "Invoices", icon: FileText },
    { path: "/pricebook", label: "Pricebook", icon: BookOpen, feature: "pricebook_inventory" },
    { path: "/service-agreements", label: "Agreements", icon: FileCheck, feature: "service_agreements" },
    { path: "/inventory", label: "Inventory", icon: Package, feature: "pricebook_inventory" },
    { path: "/reports", label: "Reports", icon: FileBarChart, feature: "reports_export" },
    { path: "/trade-iq", label: "TradeIQ", icon: TradeIQIcon, feature: "ai_foreman" },
    { path: "/archived", label: "Archived", icon: Archive },
    { path: "/settings", label: "Settings", icon: Settings },
  ];

  const technicianNavItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/jobs", label: "My Jobs", icon: Briefcase },
    { path: "/messages", label: "Messages", icon: MessageSquare },
    { path: "/trade-iq", label: "TradeIQ", icon: TradeIQIcon },
  ];

  const adminNavItems = [
    { path: "/admin", label: "Overview", icon: LayoutDashboard },
    { path: "/admin?tab=companies", label: "Companies", icon: Building2 },
    { path: "/admin?tab=users", label: "Users", icon: Users },
    { path: "/admin?tab=revenue", label: "Revenue", icon: BarChart3 },
    { path: "/admin?tab=insights", label: "Insights", icon: BarChart3 },
    { path: "/admin?tab=campaigns", label: "Campaigns", icon: FileText },
    { path: "/admin?tab=promotions", label: "Promotions", icon: Gift },
    { path: "/admin?tab=announcements", label: "Announcements", icon: FileText },
    { path: "/admin?tab=support", label: "Support Tools", icon: Briefcase },
    { path: "/admin?tab=audit", label: "Audit Log", icon: BarChart3 },
  ];

  const getNavItems = () => {
    if (user?.role === "admin") return adminNavItems;
    if (user?.role === "technician") return technicianNavItems;
    return ownerNavItems;
  };

  const navItems = getNavItems();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden">
      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src={theme === "dark" ? "/logo-dark.png?v=3" : "/logo-light.png?v=3"} 
              alt="SkilledShark" 
              className="h-10 w-auto object-contain"
            />
          </div>
          <div className="flex items-center gap-2">
            {/* Live Date/Time - Mobile (compact) */}
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="live-datetime-mobile">
              <Clock className="h-3.5 w-3.5" />
              <span className="font-medium">{formatTime(currentTime)}</span>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={toggleTheme}
              className="h-9 w-9"
              data-testid="theme-toggle-mobile"
            >
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <NotificationCenter />
            <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}>
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <Sidebar
          navItems={navItems}
          isActive={isActive}
          collapsed={collapsed}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          hasFeature={hasFeature}
          unreadMessages={unreadMessages}
          unreadPortalMessages={unreadPortalMessages}
          theme={theme}
          user={user}
          navigate={navigate}
          handleLogout={handleLogout}
        />

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-30 lg:hidden" 
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto">
          <DesktopTopBar
            collapsed={collapsed}
            toggleCollapsed={toggleCollapsed}
            theme={theme}
            toggleTheme={toggleTheme}
            currentTime={currentTime}
            formatDate={formatDate}
            formatTime={formatTime}
            setShowWhatsNew={setShowWhatsNew}
            hasNewUpdates={hasNewUpdates}
          />
          <div className="p-2 sm:p-6 lg:p-8 overflow-x-hidden max-w-full">
            {children}
          </div>
        </main>
      </div>
      <WhatsNewModal open={showWhatsNew} onOpenChange={setShowWhatsNew} />
    </div>
  );
};

export default Layout;
